import React, { useEffect, useState } from "react";
import { Avatar, Badge, Group, Table, Text, Loader, Center, Stack } from "@mantine/core";
import { supabaseClient } from "../supabase"; // Import your Supabase client

interface GuestEntry {
  id: number;
  name: string;
  crew: string;
  img_url: string | null;
}

const groupColors: Record<string, string> = {
  stroller: "#7fac9d",
  carseat: "rgba(116, 173, 207, 1)",
  diaper: "rgba(237, 161, 90, 1)",
  amazon: "rgba(168, 166, 163, 1)",
};

const crewNames: Record<string, string> = {
  stroller: "Stroller Squad",
  carseat: "Carseat Cuties",
  diaper: "Diaper Division",
  amazon: "Amazonians",
};

interface SignatureTableProps {
  refreshTrigger?: boolean; // Optional prop to trigger refresh
}

export function SignatureTable({ refreshTrigger }: SignatureTableProps) {
  const [data, setData] = useState<GuestEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Supabase
  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabaseClient.from("guest_book").select("*").order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching data:", error.message);
    } else {
      setData(data as GuestEntry[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]); // Re-fetch data when refreshTrigger changes

  const rows = data.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        <Group gap="sm" justify="center">
          <Avatar size={50} src={item.img_url || undefined} radius={30} />
          <Text fz="sm" fw={500}>
            {item.name}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap="sm" justify="center">
          <Badge color={groupColors[item.crew?.toLowerCase()] || "gray"} variant="light" radius={"sm"}>
            {crewNames[item.crew?.toLowerCase()] || "Unknown Crew"}
          </Badge>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table.ScrollContainer minWidth={200}>
      <Table verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"center"}>Name</Table.Th>
            <Table.Th ta={"center"}>Crew</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={2}>
                <Center>
                  <Loader size="sm" color={"#7fac9d"} />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : rows.length > 0 ? (
            rows
          ) : (
            <Table.Tr>
              <Table.Td colSpan={2}>
                <Center>
                  <Text>No guests have signed the book yet!</Text>
                </Center>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

interface SignatureStackProps {
  refreshTrigger?: boolean; // Optional prop to trigger refresh
}

export function SignatureStack({ refreshTrigger }: SignatureStackProps) {
  const [data, setData] = useState<GuestEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Supabase
  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabaseClient.from("guest_book").select("*").order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching data:", error.message);
    } else {
      setData(data as GuestEntry[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]); // Re-fetch data when refreshTrigger changes

  return (
    <Group justify="center">
      <Stack gap="lg">
        {loading ? (
          <Center>
            <Loader size="sm" color={"#7fac9d"} />
          </Center>
        ) : data.length > 0 ? (
          data.map((item) => (
            <Group key={item.id} gap="sm" align="flex-start">
              <Avatar size={50} src={item.img_url || undefined} radius="xl" />
              <Stack gap={2}>
                <Text fw={500} size="sm">
                  {item.name}
                </Text>
                <Badge color={groupColors[item.crew?.toLowerCase()] || "gray"} variant="light" radius={"sm"}>
                  {crewNames[item.crew?.toLowerCase()] || "Unknown Crew"}
                </Badge>
              </Stack>
            </Group>
          ))
        ) : (
          <Center>
            <Text>No guests have signed the book yet!</Text>
          </Center>
        )}
      </Stack>
    </Group>
  );
}
